import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const EVENT_TYPES = [['corporate', 'Corporate'], ['wedding', 'Wedding'], ['graduation', 'Graduation'], ['birthday', 'Family Event'], ['sports', 'Sports'], ['community', 'Community'], ['other', 'Other']];
const PACKAGES = [['visit', 'Coffee Visit'], ['small', 'Small Event'], ['medium', 'Medium Event'], ['custom', 'Custom']];
const STATUSES = [['scheduled', 'Scheduled'], ['pending', 'Pending'], ['contacted', 'Contacted'], ['completed', 'Completed']];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-espresso mb-1">{label}</label>
    {children}
  </div>
);

const SelectField = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function AddEventDialog({ open, onOpenChange, date, token, onCreated }) {
  const empty = { contact_name: '', contact_phone: '', contact_email: '', preferred_date: date, preferred_time: '', address: '', people_count: '', event_type: 'other', package_size: 'small', status: 'scheduled', notes: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) setForm({ ...empty, preferred_date: date }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleOpen = (o) => onOpenChange(o);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, people_count: Number(form.people_count) || 0, contact_email: form.contact_email.trim() || null };
      await axios.post(`${API}/admin/events`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event added to calendar');
      onCreated(form.preferred_date);
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please check the email and required fields');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-event-dialog">
        <DialogHeader><DialogTitle>Add Event</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Client name *"><Input required value={form.contact_name} onChange={(e) => set('contact_name')(e.target.value)} data-testid="add-event-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *"><Input type="date" required value={form.preferred_date} onChange={(e) => set('preferred_date')(e.target.value)} data-testid="add-event-date" /></Field>
            <Field label="Time"><Input type="time" value={form.preferred_time} onChange={(e) => set('preferred_time')(e.target.value)} data-testid="add-event-time" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><Input type="tel" value={form.contact_phone} onChange={(e) => set('contact_phone')(e.target.value)} data-testid="add-event-phone" /></Field>
            <Field label="Email (optional)"><Input type="email" value={form.contact_email} onChange={(e) => set('contact_email')(e.target.value)} data-testid="add-event-email" /></Field>
          </div>
          <Field label="Address / venue"><Input value={form.address} onChange={(e) => set('address')(e.target.value)} data-testid="add-event-address" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Guests"><Input type="number" min="0" value={form.people_count} onChange={(e) => set('people_count')(e.target.value)} data-testid="add-event-guests" /></Field>
            <Field label="Status"><SelectField value={form.status} onChange={set('status')} options={STATUSES} testId="add-event-status" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event type"><SelectField value={form.event_type} onChange={set('event_type')} options={EVENT_TYPES} testId="add-event-type" /></Field>
            <Field label="Package"><SelectField value={form.package_size} onChange={set('package_size')} options={PACKAGES} testId="add-event-package" /></Field>
          </div>
          <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => set('notes')(e.target.value)} data-testid="add-event-notes" /></Field>
          <Button type="submit" disabled={saving} className="w-full bg-burgundy hover:bg-burgundy-dark text-white rounded-full" data-testid="add-event-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save Event
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
